import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';

import Actions from '@app/store/action';
import Button from '@component/form/element/button';

const BASE_CLASS_NAME = 'cookie-banner';

export const Cookiebanner = ({
  acceptTrackers,
  declineTrackers,
  in: animIn,
  tandcAccepted,
  isPanelHalfOpen,
  isPanelFullOpen,
  isPanelErrorShown,
}) => {
  const [mounted, setMounted] = useState(false);
  const element = useRef(null);

  const className = classNames('cookie-banner', {
    'cookie-banner--hidden': !mounted || isPanelErrorShown,
    'cookie-banner--tandc-active': !tandcAccepted,
    'cookie-banner--half-panel': isPanelHalfOpen,
    'cookie-banner--full-panel': isPanelFullOpen,
  });

  const legalPageHref = '/legal/cookies';

  useEffect(() => {
    Analytics.trackSnowplowEvent({
      category: 'cookie_consent',
      action: 'show_banner',
    });

    setTimeout(() => {
      // This will make sure there is an initial animation
      setMounted(true);
    }, 200);
  }, []);

  useEffect(() => {
    const legalLinks = element.current.querySelectorAll(
      `a[href="${legalPageHref}"]`
    );
    if (legalLinks) {
      Array.from(legalLinks).forEach((legalLink) => {
        legalLink.onclick = () =>
          Analytics.trackSnowplowEvent({
            category: 'cookie_consent',
            action: 'click_legal',
          });
      });
    }
  }, [element]);

  const handleAccept = () => {
    acceptTrackers();

    Analytics.trackSnowplowEvent({
      category: 'cookie_consent',
      action: 'click_okay',
    });
  };

  const handleDecline = () => {
    declineTrackers();

    Analytics.trackSnowplowEvent({
      category: 'cookie_consent',
      action: 'click_nope',
    });
  };

  const content = {
    __html: I18n.t('transfer.cookie-banner.text', { href: legalPageHref }),
  };

  return (
    <CSSTransition
      in={animIn}
      timeout={200}
      classNames={{
        exit: `${BASE_CLASS_NAME}--hidden`,
      }}
      unmountOnExit
    >
      <div className={className}>
        <div className="cookie-banner__content">
          <div
            className="cookie-banner__text"
            ref={element}
            dangerouslySetInnerHTML={content}
          />
          <div className="cookie-banner__buttons">
            <Button
              text={I18n.t('transfer.cookie-banner.button-decline')}
              className="cookie-banner__button cookie-banner__button--decline"
              onClick={handleDecline}
            />
            <Button
              text={I18n.t('transfer.cookie-banner.button-accept')}
              className="cookie-banner__button cookie-banner__button--accept"
              onClick={handleAccept}
            />
          </div>
        </div>
      </div>
    </CSSTransition>
  );
};

Cookiebanner.propTypes = {
  acceptTrackers: PropTypes.func.isRequired,
  declineTrackers: PropTypes.func.isRequired,
  in: PropTypes.bool,
  tandcAccepted: PropTypes.bool,
  isPanelHalfOpen: PropTypes.bool,
  isPanelFullOpen: PropTypes.bool,
  isPanelErrorShown: PropTypes.bool,
};

const mapStateToProps = ({ route, notification }) => ({
  isPanelHalfOpen: route.isPanelHalfOpen,
  isPanelFullOpen: route.isPanelFullOpen,
  isPanelErrorShown: notification.panel.category === 'error',
});

const mapDispatchToProps = (dispatch) => ({
  acceptTrackers: () => dispatch(Actions.Session.acceptTrackers()),
  declineTrackers: () => dispatch(Actions.Session.declineTrackers()),
});

const ConnectedCookiebanner = connect(
  mapStateToProps,
  mapDispatchToProps
)(Cookiebanner);

ConnectedCookiebanner.propTypes = {
  in: PropTypes.bool,
  tandcAccepted: PropTypes.bool,
};

export default ConnectedCookiebanner;
